import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from './constants'
import { getCounterpartiesOptions } from '../../api/getCounterpartiesOptions'

export enum CounterpartyType {
    Person = 0,
    Company = 1,
    All = -1,
}

export interface UseCounterpartyAsyncAutocompleteOptionsParams {
    page?: number
    size?: number
    search_param?: string
    order_by?: string
    order_asc?: boolean
    type_filter?: CounterpartyType
    black_list_filter?: boolean
}

export const defaultOptions = {
    order_by: 'created_at',
    order_asc: false,
    type_filter: CounterpartyType.All,
    black_list_filter: false,
}

export const useCounterpartyAsyncAutocompleteOptions = (
    options?: UseCounterpartyAsyncAutocompleteOptionsParams,
) => {
    const keysToWatch = options ? Object.values(options) : []
    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isLoading,
    } = useInfiniteQuery({
        queryKey: [QueryKeys.GetAllCounterpartiesQueryOptions, ...keysToWatch],
        queryFn: ({ pageParam }) =>
            getCounterpartiesOptions({
                ...defaultOptions,
                ...options,
                page: pageParam,
            }),
        initialPageParam: 1,
        getNextPageParam: lastPage => {
            const currentPage = lastPage.pagination.current_page
            const totalPages = lastPage.pagination.total_pages

            if (currentPage === totalPages) {
                return
            }
            return currentPage + 1
        },
        getPreviousPageParam: firstPage => {
            const currentPage = firstPage.pagination.current_page

            if (currentPage === 1) {
                return
            }
            return currentPage - 1
        },
    })

    const counterparties = data?.pages?.map(page => page.counterparties)?.flat()

    return {
        counterparties,
        isLoading,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isFetching: isFetchingNextPage || isFetchingPreviousPage,
    }
}
